import { useNavigate } from 'react-router-dom';
import { DollarSign, Wrench, MessageSquare, FileText, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import StatCard from '../../components/ui/StatCard';
import Badge from '../../components/ui/Badge';
import { useRenterDashboard } from '../../hooks/useDashboard';
import { RecentActivityFeed } from '../../components/ui/RecentActivityFeed';
import { DashboardSkeleton } from '../../components/design-system/EmptyState';

const RenterDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, isError, error, refetch } = useRenterDashboard();

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="flex items-center gap-2 text-coral-600">
          <AlertCircle size={20} />
          <span className="text-sm font-medium">{(error as Error)?.message || 'Failed to load dashboard data. Please try again.'}</span>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-10 text-sm font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-6">

      {/* Hero payment card */}
      <div
        className="rounded-16 p-6 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, var(--forest-900) 0%, var(--forest-700) 100%)' }}
      >
        <div className="relative z-10">
          <p className="text-[13px] text-white/70 mb-0.5">
            {greeting()}, {user?.firstName || user?.name || 'there'}
          </p>
          <h1 className="text-xl font-semibold mb-4">Your Tenancy</h1>
          <div className="flex flex-wrap gap-8 items-end">
            <div>
              <p className="text-white/60 text-[11px] uppercase tracking-wider mb-0.5">Next Rent Due</p>
              <p className="text-3xl font-semibold tabular-nums">
                {data?.nextRentAmount ? `$${data.nextRentAmount.toFixed(2)}` : '—'}              </p>
              <p className="text-white/70 text-[12px] mt-0.5">
                {data?.nextDueDate
                  ? `Due ${new Date(data.nextDueDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : 'No upcoming payment'}
              </p>
            </div>
            <div>
              <p className="text-white/60 text-[11px] uppercase tracking-wider mb-0.5">Lease Ends</p>
              <p className="text-lg font-semibold">12 Aug 2025</p>
            </div>
            <div className="flex-1 min-w-[180px]">
              <p className="text-white/60 text-[11px] uppercase tracking-wider mb-1.5">Lease Progress</p>
              <div className="w-full h-1.5 rounded-full bg-white/20 overflow-hidden">
                <div className="h-full rounded-full bg-teal-400" style={{ width: '54%' }} />
              </div>
              <p className="text-white/60 text-[11px] mt-1">54% complete</p>
            </div>
          </div>
          <div className="mt-5">
            <button
              onClick={() => navigate('/renter/pay-rent')}
              className="px-5 py-2.5 rounded-10 text-sm font-semibold text-forest-900 transition-opacity hover:opacity-90"
              style={{ background: 'var(--teal-400)' }}
            >
              Pay Rent Now
            </button>
          </div>
        </div>
        {/* subtle bg pattern */}
        <div
          className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-10"
          style={{ background: 'var(--teal-400)' }}
        />
        <div
          className="absolute -right-4 bottom-0 w-24 h-24 rounded-full opacity-10"
          style={{ background: 'var(--teal-300)' }}
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Active Leases"        value={data?.activeLeases ?? 0} />
        <StatCard label="Upcoming Payments"    value={data?.upcomingPayments ?? 0} tone="info" />
        <StatCard label="Overdue Payments"     value={data?.overduePayments ?? 0} tone={data?.overduePayments ? 'danger' : 'default'} />
        <StatCard label="Open Maintenance"     value={data?.openMaintenanceRequests ?? 0} tone="warn" />
      </div>

      {/* Quick actions */}
      <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
        <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {([
            { icon: <DollarSign size={18} />, label: 'Pay Rent', to: '/renter/pay-rent', tone: 'forest' },
            { icon: <Wrench size={18} />, label: 'Maintenance', to: '/maintenance/new', tone: 'teal' },
            { icon: <FileText size={18} />, label: 'Documents', to: '/documents', tone: 'neutral' },
            { icon: <MessageSquare size={18} />, label: 'Messages', to: '/messages', tone: 'neutral' },
          ] as const).map(a => (
            <button
              key={a.to}
              onClick={() => navigate(a.to)}
              className="flex flex-col items-center gap-2 p-4 rounded-10 border border-ink-200 text-[13px] font-medium text-ink-800 hover:border-forest-900 hover:bg-forest-50 transition-colors"
            >
              <span className="text-forest-900">{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Maintenance + Activity split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider">Maintenance</h2>
            <button
              onClick={() => navigate('/maintenance/track')}
              className="text-[12px] text-forest-900 font-medium hover:underline"
            >
              View all
            </button>
          </div>
          {(data?.openMaintenanceRequests ?? 0) > 0 ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-8 bg-warn-50 border border-yellow-200">
                <Wrench size={16} className="text-warn-700 flex-none mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-ink-900">Open requests</p>
                  <p className="text-[12px] text-ink-600 mt-0.5">{data?.openMaintenanceRequests} active maintenance request(s)</p>
                </div>
                <Badge tone="warn">{data?.openMaintenanceRequests}</Badge>
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-ink-500 py-4 text-center">No open maintenance requests</p>
          )}
          <button
            onClick={() => navigate('/maintenance/new')}
            className="mt-4 w-full py-2 rounded-8 border-2 border-dashed border-ink-300 text-[12px] font-medium text-ink-600 hover:border-forest-900 hover:text-forest-900 transition-colors"
          >
            + New Request
          </button>
        </div>

        <RecentActivityFeed limit={5} />
      </div>
    </div>
  );
};

export default RenterDashboard;
